
import { useState } from 'react';
import { User, Session } from '../types';
import { useMfaActions } from '../mfa/useMfaActions';
import { 
  performLogin, 
  performProviderLogin, 
  performSignup, 
  performLogout, 
  performProfileUpdate 
} from '../operations';

interface UseAuthOperationsProps {
  setUser: (user: User | null) => void;
  setSession: (session: Session | null) => void;
}

export function useAuthOperations({ setUser, setSession }: UseAuthOperationsProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const mfaActions = useMfaActions();

  // Function to handle login with email and password
  const login = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { user, session } = await performLogin(email, password);
      setUser(user);
      setSession(session);
      
      // Check if user has MFA enabled and initiate challenge if needed
      const mfaRequired = await mfaActions.initiateMfaChallenge();
      console.log('MFA required:', mfaRequired);
      
      return { user, session, weakPassword: { isWeak: false } };
    } catch (err) {
      console.error('❌ Login error:', err);
      const error = err instanceof Error ? err : new Error('An unknown error occurred during login');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
      console.log('🏁 Login process finished');
    }
  };

  // Function to handle login with third-party providers
  const loginWithProvider = async (provider: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      await performProviderLogin(provider);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(`Failed to login with ${provider}`);
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Function to handle signup with email and password
  const signup = async (email: string, password: string, metadata?: Record<string, any>) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { user, session } = await performSignup(email, password, metadata);
      setUser(user);
      setSession(session);
      return { user, session };
    } catch (err) {
      const error = err instanceof Error ? err : new Error('An unknown error occurred during signup');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Function to handle logout
  const logout = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      await performLogout();
      mfaActions.resetMfaState(); // Reset MFA state on logout
      setUser(null);
      setSession(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('An unknown error occurred during logout');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Function to update user profile
  const updateUserProfile = async (values: Record<string, any>) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { updatedUser, result } = await performProfileUpdate(values);
      if (updatedUser) {
        setUser(updatedUser);
      }
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('An unknown error occurred during profile update');
      setError(error);
      console.error('Profile update error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    mfaActions,
    login,
    loginWithProvider,
    logout,
    signup,
    updateUserProfile,
    error
  };
}
